<script setup>
import { onMounted, nextTick, onBeforeUnmount, watch, computed } from 'vue';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
  TitleComponent,
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { ButtonComponent } from '@/components';

echarts.use([
  TooltipComponent,
  LegendComponent,
  GridComponent,
  TitleComponent,
  PieChart,
  CanvasRenderer,
  LabelLayout,
]);

const props = defineProps({
  chartId: {
    type: String,
    required: false,
    default: 'doughnut-chart',
  },
  data: {
    type: Array,
    required: true,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  style: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  saveImage: {
    type: Boolean,
    required: false,
    default: false,
  },
  showLinkButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  link: {
    type: String,
    required: false,
    default: '',
  },
  customHeight: {
    type: Boolean,
    required: false,
    default: false,
  },
  centerText: {
    type: String,
    required: false,
    default: '',
  },
  title: {
    type: String,
    required: false,
    default: '',
  },
  subTitle: {
    type: String,
    required: false,
    default: '',
  },
  hasInnerShadow: {
    type: Boolean,
    required: false,
    default: false,
  },
});
// let option;
let myChart;

onMounted(() => {
  const chartDom = document.getElementById(props.chartId);
  if (!chartDom) {
    console.error(`Chart container with id "${props.chartId}" not found.`);
    return;
  }
  nextTick(() => {
    myChart = echarts.init(chartDom);
    option.value && myChart.setOption(option.value);
    myChart.resize();
  });
});

onBeforeUnmount(() => {
  if (myChart) {
    myChart.dispose();
  }
});

const option = computed(() => ({
  tooltip: {
    trigger: 'item',
    formatter: function (params) {
      const formattedValue = params?.value?.toLocaleString() || 0;
      return `${formattedValue} (${params?.percent}%)`;
    },
  },
  title: {
    text: props.title,
    subtext: props.subTitle,
    left: 'center',
    top: 'center',
    itemGap: -8,
    textStyle: {
      fontFamily: 'Poppins', // or any valid font
      fontSize: 28,
      fontWeight: 'bold',
    },
    subtextStyle: {
      fontFamily: 'Poppins', // or any valid font
      fontSize: 10,
      fontWeight: 'normal',
    },
  },
  series: [
    {
      name: 'Access From',
      type: 'pie',
      radius: ['70%', '95%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 5,
        borderWidth: 2,
        borderColor: '#fff',
      },
      padAngle: 1,
      emphasis: {
        scale: true,
        scaleSize: 3,
        label: {
          show: false,
          fontSize: 40,
          fontWeight: 'bold',
        },
      },
      labelLine: {
        show: false,
      },
      color: props.style.colors,
      data: props.data,
    },
  ],
}));

watch(
  option,
  (newVal) => {
    if (newVal && myChart) {
      myChart.setOption(newVal);
    }
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="relative flex h-full w-full flex-col items-center justify-center">
    <div
      v-if="hasInnerShadow"
      class="pointer-events-none absolute left-1/2 top-1/2 z-[1] h-24 w-24 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-transparent shadow-[0px_0.8500000238418579px_9.350000381469727px_0px_rgba(120,120,120,0.19)]"
    ></div>
    <div
      :id="chartId"
      class="relative"
      :class="style?.size || 'h-full w-full'"
    ></div>
    <RouterLink
      :to="link || '/'"
      v-if="showLinkButton && !isLoading"
      class="w-full"
    >
      <ButtonComponent
        variant="transparent"
        class="flex h-10 w-full justify-center rounded-t-none border-t border-steel-300"
      >
        View Details
      </ButtonComponent>
    </RouterLink>
  </div>
</template>

<style scoped></style>
